import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Text,
  Title,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { IconAlertCircle, IconChevronRight, IconEye, IconEyeOff, IconMail, IconPhone } from '@tabler/icons-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { Shema } from '../api/odjemalec';
import { sporocilo } from '../api/napaka';
import { evri, koliko, mesecKratko, obdobje, preteklo } from '../oblika/oblika';
import { Kartica } from '../postavitev/Podatki';
import { PomocIkona } from '../pomoc/PomocIkona';
import { novih } from '../povprasevanja/povprasevanja';
import { barve } from '../tema';
import { SlikaVozila } from '../vozila/SlikaVozila';
import {
  KARTICE_ZACETNE,
  neogledano,
  rokPredracuna,
  usePregledPoslovanja,
  type KarticaZacetne,
  type Nastavitve,
} from './zacetna';

type Pregled = Shema['PregledPoslovanja'];
type Mesec = Shema['MesecPoslovanja'];

const VOZIL: [string, string, string, string] = ['vozilo', 'vozili', 'vozila', 'vozil'];
const OGLEDOV: [string, string, string, string] = ['ogled', 'ogleda', 'ogledi', 'ogledov'];
const POVPRASEVANJ: [string, string, string, string] = [
  'povpraševanje',
  'povpraševanji',
  'povpraševanja',
  'povpraševanj',
];

/**
 * Pregled poslovanja na začetnem zaslonu (nov v spletu, Swing ga nima): številke tekočega meseca, graf zadnjih 12
 * mesecev, odprta povpraševanja, predračuni pred iztekom (±2 dni) in vozila, za katera se kupci največ zanimajo.
 * Vse izračuna strežnik ({@code GET /api/zacetna/pregled}); na telefonu je vse v enem stolpcu, številke po dve v vrsti.
 * Kaj je prikazano, uporabnik izbere v oknu »Prilagodi« ({@code PrilagoditevZacetne}); »Skrij številke« je bližnjica
 * za številke in graf (npr. ko stranka vidi zaslon). Skrita kartica z neogledanim povpraševanjem ({@link neogledano})
 * se pokaže vseeno, z opombo. {@code nastavitve} so osebne nastavitve s strežnika, {@code undefined} med nalaganjem –
 * takrat številk in kartic še ne kažemo, da se skrite ne pokažejo za trenutek.
 */
export function PregledPoslovanja({
  nastavitve,
  shrani,
}: {
  nastavitve: Nastavitve | undefined;
  shrani: (n: Nastavitve) => void;
}) {
  return (
    <Stack gap="md">
      <Group justify="space-between" align="center" gap="xs" wrap="nowrap">
        <Group gap={4} wrap="nowrap" style={{ minWidth: 0 }}>
          <Title order={2} size="h4" c="white" style={{ whiteSpace: 'nowrap' }}>
            Pregled poslovanja
          </Title>
          <PomocIkona clanek="zacetna-stran" naTemnem />
        </Group>
        {nastavitve && (
          <Button
            variant="subtle"
            color="gray.0"
            size="compact-md"
            leftSection={nastavitve.skrijPregled ? <IconEye size={18} /> : <IconEyeOff size={18} />}
            onClick={() => shrani({ ...nastavitve, skrijPregled: !nastavitve.skrijPregled })}
            style={{ flexShrink: 0 }}
          >
            {nastavitve.skrijPregled ? 'Prikaži številke' : 'Skrij številke'}
          </Button>
        )}
      </Group>
      <Vsebina nastavitve={nastavitve} />
    </Stack>
  );
}

function Vsebina({ nastavitve }: { nastavitve: Nastavitve | undefined }) {
  const pregled = usePregledPoslovanja(true);
  if (pregled.error) {
    return (
      <Alert
        color="red"
        variant="filled"
        radius="lg"
        icon={<IconAlertCircle size={20} />}
        title="Pregleda ni bilo mogoče naložiti"
      >
        <Text size="sm">{sporocilo(pregled.error)}</Text>
        <Button size="xs" variant="white" color="red" mt="sm" onClick={() => void pregled.refetch()}>
          Poskusi znova
        </Button>
      </Alert>
    );
  }
  if (!pregled.data || !nastavitve) {
    return (
      <Center py={60}>
        <Loader color="gray.0" aria-label="Nalagam pregled" />
      </Center>
    );
  }
  const p = pregled.data;
  const skrite = nastavitve.skriteKartice;
  const prikazane = KARTICE_ZACETNE.filter(({ k }) => !skrite.includes(k) || neogledano(k, p));
  const kartica = (k: KarticaZacetne) => {
    const opomba = skrite.includes(k)
      ? 'Skrita kartica – prikazana zaradi neogledanih povpraševanj. Skrijete jo v Prilagodi.'
      : undefined;
    if (k === 'POVPRASEVANJA') {
      return <Povprasevanja key={k} p={p} opomba={opomba} />;
    }
    return k === 'PREDRACUNI' ? (
      <Predracuni key={k} p={p} opomba={opomba} />
    ) : (
      <Zanimanje key={k} p={p} opomba={opomba} />
    );
  };
  const n = prikazane.length;
  return (
    <Stack gap="md">
      {!nastavitve.skrijPregled && (
        <>
          <Stevilke p={p} />
          <GrafMesecev meseci={p.meseci ?? []} />
        </>
      )}
      {n > 0 && (
        <SimpleGrid cols={{ base: 1, md: Math.min(n, 2), xl: n }} spacing="md">
          {prikazane.map(({ k }) => kartica(k))}
        </SimpleGrid>
      )}
    </Stack>
  );
}

/** Opomba na vrhu kartice, zakaj je skrita kartica vseeno prikazana. */
function Opomba({ besedilo }: { besedilo?: string }) {
  return besedilo ? (
    <Text size="xs" c={barve.besediloSekundarno} mb={8}>
      {besedilo}
    </Text>
  ) : null;
}

function Stevilke({ p }: { p: Pregled }) {
  const meseci = p.meseci ?? [];
  const ta = meseci[meseci.length - 1];
  const prej = meseci[meseci.length - 2];
  return (
    <SimpleGrid cols={{ base: 2, md: 4 }} spacing={{ base: 'sm', sm: 'md' }}>
      <Stevilka
        naslov="Prodaja ta mesec"
        na="/analize"
        vrednost={koliko(ta?.prodanaVozila ?? 0, VOZIL)}
        opis={`promet ${evri(ta?.prihodki ?? 0)}`}
        primerjava={`prejšnji mesec: ${koliko(prej?.prodanaVozila ?? 0, VOZIL)}, ${evri(prej?.prihodki ?? 0)}`}
      />
      <Stevilka
        naslov="Zaslužek ta mesec"
        na="/analize"
        vrednost={evri(ta?.razlika ?? 0)}
        opis="prihodki − odhodki"
        primerjava={`prejšnji mesec: ${evri(prej?.razlika ?? 0)}`}
      />
      <Stevilka
        naslov="Zaloga"
        na="/vozila"
        vrednost={koliko(p.zalogaVozil ?? 0, VOZIL)}
        opis={`vrednost ${evri(p.zalogaVrednost ?? 0)}`}
        primerjava={
          p.zalogaVozil
            ? `povprečno ${koliko(p.zalogaPovprecnoDni ?? 0, ['dan', 'dneva', 'dnevi', 'dni'])} na zalogi`
            : undefined
        }
      />
      <Stevilka
        naslov="Povpraševanja"
        na="/povprasevanja"
        vrednost={novih(p.povprasevanjaNova ?? 0)}
        opis={`odprtih skupaj: ${p.povprasevanjaOdprta ?? 0}`}
        poudarjeno={(p.povprasevanjaNova ?? 0) > 0}
      />
    </SimpleGrid>
  );
}

function Stevilka({
  naslov,
  vrednost,
  opis,
  primerjava,
  na,
  poudarjeno = false,
}: {
  naslov: string;
  vrednost: string;
  opis: string;
  primerjava?: string;
  na: string;
  poudarjeno?: boolean;
}) {
  return (
    <UnstyledButton
      component={Link}
      to={na}
      aria-label={`${naslov}: ${vrednost}, ${opis}`}
      style={{ display: 'block' }}
    >
      <Paper
        radius="lg"
        p={{ base: 'sm', sm: 'md' }}
        shadow="md"
        h="100%"
        style={poudarjeno ? { boxShadow: `inset 4px 0 0 ${barve.poudarek}` } : undefined}
      >
        <Text size="sm" c={barve.besediloSekundarno} fw={600} truncate>
          {naslov}
        </Text>
        <Text fw={700} c={barve.besedilo} lh={1.2} mt={4} fz={{ base: 21, sm: 28 }} style={{ whiteSpace: 'nowrap' }}>
          {vrednost}
        </Text>
        <Text size="sm" c={barve.besedilo} mt={2}>
          {opis}
        </Text>
        {primerjava && (
          <Text size="xs" c={barve.besediloSekundarno} mt={4}>
            {primerjava}
          </Text>
        )}
      </Paper>
    </UnstyledButton>
  );
}

const MERE = [
  { kljuc: 'prihodki', naziv: 'Prihodki', vrednost: (m: Mesec) => m.prihodki ?? 0, oblika: evri },
  { kljuc: 'razlika', naziv: 'Zaslužek', vrednost: (m: Mesec) => m.razlika ?? 0, oblika: evri },
  {
    kljuc: 'vozila',
    naziv: 'Vozila',
    vrednost: (m: Mesec) => m.prodanaVozila ?? 0,
    oblika: (n: number) => koliko(n, ['prodano vozilo', 'prodani vozili', 'prodana vozila', 'prodanih vozil']),
  },
] as const;

const VISINA_GRAFA = 170;

/**
 * Stolpčni graf zadnjih 12 mesecev, en niz naenkrat (izbira prihodki / zaslužek / prodana vozila), zato brez legende
 * in z eno osjo; negativni zaslužek gre pod osnovnico. Podrobnosti ob prehodu z miško ali dotiku, tabela v Analizah.
 */
function GrafMesecev({ meseci }: { meseci: Mesec[] }) {
  const [kljuc, setKljuc] = useState<string>('prihodki');
  const mera = MERE.find((m) => m.kljuc === kljuc) ?? MERE[0];
  const vrednosti = meseci.map(mera.vrednost);
  const najvec = Math.max(0, ...vrednosti);
  const najmanj = Math.min(0, ...vrednosti);
  const razpon = najvec - najmanj || 1;
  // delež višine nad osnovnico; brez prometa je osnovnica spodaj
  const nad = najvec - najmanj > 0 ? najvec / razpon : 1;
  return (
    <Kartica naslov="Zadnjih 12 mesecev">
      {/* izbira v vsebini, ne v glavi kartice, da se naslov na telefonu ne lomi */}
      <Group justify="space-between" align="center" gap="xs" mb={8} wrap="wrap-reverse">
        <Text size="xs" c={barve.besediloSekundarno}>
          največ {mera.oblika(najvec)}
          {najmanj < 0 ? ` · najmanj ${mera.oblika(najmanj)}` : ''}
        </Text>
        <SegmentedControl
          size="xs"
          value={mera.kljuc}
          onChange={setKljuc}
          data={MERE.map((m) => ({ value: m.kljuc, label: m.naziv }))}
          aria-label="Prikaži v grafu"
        />
      </Group>
      <Box
        role="group"
        aria-label={`${mera.naziv} po mesecih`}
        style={{ display: 'flex', gap: 4, height: VISINA_GRAFA, position: 'relative' }}
      >
        <Box
          aria-hidden
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: `${nad * 100}%`,
            borderTop: `1px solid ${barve.obroba}`,
          }}
        />
        {meseci.map((m, i) => {
          const v = vrednosti[i]!;
          const visina = (Math.abs(v) / razpon) * 100;
          const besedilo = `${obdobje(m.mesec)}: ${mera.oblika(v)}`;
          return (
            <Tooltip key={m.mesec} label={besedilo} withArrow events={{ hover: true, focus: true, touch: true }}>
              <Box
                role="img"
                aria-label={besedilo}
                tabIndex={0}
                style={{ flex: 1, minWidth: 0, position: 'relative', height: '100%', cursor: 'default' }}
              >
                <Box
                  style={{
                    position: 'absolute',
                    left: '12%',
                    right: '12%',
                    background: barve.primarna,
                    ...(v >= 0
                      ? { bottom: `${(1 - nad) * 100}%`, height: `${visina}%`, borderRadius: '4px 4px 0 0' }
                      : { top: `${nad * 100}%`, height: `${visina}%`, borderRadius: '0 0 4px 4px' }),
                    minHeight: v !== 0 ? 2 : 0,
                  }}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      <Box style={{ display: 'flex', gap: 4, marginTop: 6 }} aria-hidden>
        {meseci.map((m, i) => (
          <Text
            key={m.mesec}
            size="xs"
            ta="center"
            c={i === meseci.length - 1 ? barve.besedilo : barve.besediloSekundarno}
            fw={i === meseci.length - 1 ? 700 : 400}
            style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
          >
            {mesecKratko(m.mesec)}
          </Text>
        ))}
      </Box>
    </Kartica>
  );
}

function Povprasevanja({ p, opomba }: { p: Pregled; opomba?: string }) {
  const seznam = p.povprasevanja ?? [];
  return (
    <Kartica naslov="Povpraševanja" dejanja={<Vsa na="/povprasevanja" naziv="Vsa povpraševanja" besedilo="Vsa" />}>
      <Opomba besedilo={opomba} />
      {!seznam.length && <Prazno>Ni odprtih povpraševanj.</Prazno>}
      <Stack gap={0}>
        {seznam.map((q) => (
          <Vrstica key={q.id}>
            <UnstyledButton component={Link} to={`/povprasevanja/${q.id}`} style={{ flex: 1, minWidth: 0 }}>
              <Group gap={6} wrap="nowrap">
                <Text fw={600} truncate>
                  {q.ime}
                </Text>
                {q.stanje === 'NOVO' && (
                  <Badge color="red" size="sm" style={{ flexShrink: 0 }}>
                    novo
                  </Badge>
                )}
              </Group>
              <Text size="sm" c="dimmed" truncate>
                {q.voziloNaziv || 'Splošno povpraševanje'} · {preteklo(q.ustvarjeno)}
              </Text>
            </UnstyledButton>
            {q.telefon && (
              <ActionIcon
                component="a"
                href={`tel:${q.telefon}`}
                variant="light"
                size={40}
                aria-label={`Pokliči ${q.ime}`}
              >
                <IconPhone size={20} />
              </ActionIcon>
            )}
            {q.email && (
              <ActionIcon
                component="a"
                href={`mailto:${q.email}`}
                variant="light"
                size={40}
                aria-label={`Piši ${q.ime}`}
              >
                <IconMail size={20} />
              </ActionIcon>
            )}
          </Vrstica>
        ))}
      </Stack>
    </Kartica>
  );
}

function Predracuni({ p, opomba }: { p: Pregled; opomba?: string }) {
  const seznam = p.predracuni ?? [];
  return (
    <Kartica naslov="Predračuni pred iztekom" dejanja={<Vsa na="/predracuni" naziv="Vsi predračuni" besedilo="Vsi" />}>
      <Opomba besedilo={opomba} />
      {!seznam.length && <Prazno>Noben predračun brez računa ne poteče v 2 dneh.</Prazno>}
      <Stack gap={0}>
        {seznam.map((r) => {
          const rok = rokPredracuna(r.veljavnoDo);
          return (
            <Vrstica key={r.id} na={`/predracuni/${r.id}`}>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text fw={600} truncate>
                  {r.kupec}
                </Text>
                <Text size="sm" c="dimmed" truncate>
                  {r.stPredracuna} · {evri(r.znesek)}
                </Text>
              </Box>
              <Badge color={rok.poteklo ? 'red' : 'yellow'} variant="light" size="md" style={{ flexShrink: 0 }}>
                {rok.besedilo}
              </Badge>
            </Vrstica>
          );
        })}
      </Stack>
    </Kartica>
  );
}

function Zanimanje({ p, opomba }: { p: Pregled; opomba?: string }) {
  const seznam = p.zanimanje ?? [];
  return (
    <Kartica naslov="Zanimanje kupcev" dejanja={<Vsa na="/vozila" naziv="Vsa vozila" besedilo="Vsa" />}>
      <Opomba besedilo={opomba} />
      {!seznam.length && <Prazno>V zadnjih 7 dneh ni ogledov javne ponudbe ne odprtih povpraševanj za vozila.</Prazno>}
      <Stack gap={0}>
        {seznam.map((v) => (
          <Vrstica key={v.id} na={`/vozila/${v.id}`}>
            <SlikaVozila voziloId={v.id!} slikaId={v.slikaId} sirina={56} />
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Text fw={600} truncate>
                {v.naziv}
              </Text>
              <Text size="sm" fw={600}>
                {evri(v.cena)}
              </Text>
              <Text size="xs" c="dimmed" truncate>
                {koliko(v.ogledi7Dni ?? 0, OGLEDOV)} v 7 dneh
                {v.povprasevanja ? ` · ${koliko(v.povprasevanja, POVPRASEVANJ)}` : ''}
              </Text>
            </Box>
          </Vrstica>
        ))}
      </Stack>
    </Kartica>
  );
}

/** Vrstica seznama v kartici: ločena s črto, dovolj visoka za prst; z {@code na} je cela vrstica povezava. */
function Vrstica({ children, na }: { children: ReactNode; na?: string }) {
  const vsebina = (
    <Group gap="sm" wrap="nowrap" py={10} mih={56} style={{ borderTop: `1px solid ${barve.obroba}` }}>
      {children}
      {na && <IconChevronRight size={18} color={barve.besediloSekundarno} style={{ flexShrink: 0 }} />}
    </Group>
  );
  return na ? (
    <UnstyledButton component={Link} to={na} style={{ display: 'block' }}>
      {vsebina}
    </UnstyledButton>
  ) : (
    vsebina
  );
}

function Vsa({ na, naziv, besedilo }: { na: string; naziv: string; besedilo: string }) {
  return (
    <Button component={Link} to={na} variant="subtle" size="compact-sm" aria-label={naziv}>
      {besedilo}
    </Button>
  );
}

function Prazno({ children }: { children: ReactNode }) {
  return (
    <Text size="sm" c="dimmed">
      {children}
    </Text>
  );
}
